import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import ReCAPTCHA from 'react-google-recaptcha';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Search, FileText, CheckCircle, Clock, AlertCircle, 
  Loader2, Building, User, Calendar, Scale, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const ESTADOS_RECLAMO = {
  'NUEVO': { label: 'Nuevo', color: 'bg-blue-100 text-blue-800', icon: Clock },
  'EN_PROCESO': { label: 'En Proceso', color: 'bg-amber-100 text-amber-800', icon: Clock },
  'TERMINADO': { label: 'Terminado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
};

const ConsultaReclamoPage = ({ empresaCodigoProp }) => {
  const params = useParams();
  const empresaCodigo = empresaCodigoProp || params.empresaCodigo;
  const recaptchaRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [empresa, setEmpresa] = useState(null);
  const [folio, setFolio] = useState('');
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');

  // Cargar datos de la empresa
  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/empresa/${empresaCodigo}`);
        if (response.ok) {
          const data = await response.json();
          setEmpresa(data);
          
          // Aplicar colores del tema
          if (data.colores_tema || data.colores) {
            applyThemeColors(data.colores_tema || data.colores);
          }
        } else {
          setError('Empresa no encontrada');
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    if (empresaCodigo) {
      fetchEmpresa();
    }
  }, [empresaCodigo]);

  const applyThemeColors = (colores) => {
    const root = document.documentElement;
    if (colores.primario) root.style.setProperty('--color-primario', colores.primario);
    if (colores.secundario) root.style.setProperty('--color-secundario', colores.secundario);
    if (colores.terciario) root.style.setProperty('--color-terciario', colores.terciario);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setResultado(null);

    if (!folio.trim()) {
      setError('Ingrese el número de folio');
      return;
    }

    if (!recaptchaToken) {
      setError('Complete la verificación de seguridad');
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(
        `${API_URL}/api/consulta/${empresaCodigo}/${folio.trim()}?recaptcha_token=${encodeURIComponent(recaptchaToken)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setResultado(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Reclamo no encontrado');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Error al buscar el reclamo');
    } finally {
      setSearching(false);
      // Reset reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken('');
    }
  };

  const getEstadoBadge = (estado) => {
    const config = ESTADOS_RECLAMO[estado] || { label: estado, color: 'bg-gray-100 text-gray-800', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1 text-sm px-3 py-1`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!empresa) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Empresa no encontrada</h2>
            <p className="text-slate-600">
              El código de empresa "{empresaCodigo}" no existe o no está disponible.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="consulta-reclamo-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-center gap-4">
          {empresa.logo_url && (
            <img 
              src={empresa.logo_url} 
              alt={empresa.nombre}
              className="h-12 object-contain"
            />
          )}
          <div className="text-center">
            <h1 className="text-xl font-bold text-slate-800">{empresa.nombre}</h1>
            <p className="text-sm text-slate-500">Consulta de Reclamos</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Formulario de búsqueda */}
        <Card className="border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5 text-slate-500" />
              Consultar Estado de Reclamo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="folio">Número de Folio</Label>
                <Input
                  id="folio"
                  value={folio}
                  onChange={(e) => setFolio(e.target.value.toUpperCase())}
                  placeholder="Ej: PROJ-0000001"
                  className="font-mono"
                  data-testid="folio-input"
                />
                <p className="text-xs text-slate-500">
                  Ingrese el número de folio que recibió al registrar su reclamo
                </p>
              </div>

              {/* reCAPTCHA */}
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken('')}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={searching || !recaptchaToken}
                style={{ backgroundColor: (empresa.colores_tema || empresa.colores)?.primario || '#4A6B6B' }}
                data-testid="btn-buscar"
              >
                {searching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Buscando...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Buscar Reclamo
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Resultado de la búsqueda */}
        {resultado && (
          <div className="space-y-4 animate-fadeIn">
            {/* Información del Reclamo */}
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-500" />
                    Información del Reclamo
                  </CardTitle>
                  {getEstadoBadge(resultado.resolucion ? 'TERMINADO' : resultado.reclamo.estado)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Folio</p>
                      <p className="font-mono font-semibold">{resultado.reclamo.folio}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Reclamante</p>
                      <p className="font-medium">{resultado.reclamo.nombre}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Fecha de Registro</p>
                      <p className="font-medium">
                        {format(new Date(resultado.reclamo.fecha_registro), "dd 'de' MMMM 'de' yyyy", { locale: es })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Building className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Proyecto</p>
                      <p className="font-medium">{resultado.reclamo.proyecto}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resolución (si existe) */}
            {resultado.resolucion ? (
              <Card className="border-green-200 bg-green-50/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                    <Scale className="w-5 h-5" />
                    Resolución
                    <Badge className="bg-green-100 text-green-800 ml-2">Terminado</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border border-green-200 text-slate-700 overflow-hidden break-words max-w-full [&_*]:max-w-full [&_table]:w-full [&_img]:max-w-full [&_p]:mb-2 [&_br]:block [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-bold [&_em]:italic [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-bold [&_h3]:mb-1"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resultado.resolucion.contenido || '', {
                      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'style', 'link', 'meta'],
                      FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'srcdoc'],
                      ALLOW_DATA_ATTR: false,
                    }) }}
                  />
                  {resultado.resolucion.fecha_aprobacion && (
                    <p className="text-sm text-green-700">
                      Resolución emitida el {format(new Date(resultado.resolucion.fecha_aprobacion), "dd 'de' MMMM 'de' yyyy", { locale: es })}
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-8 h-8 text-amber-500 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-amber-800 mb-1">
                        Resolución en proceso
                      </h3>
                      <p className="text-amber-700 text-sm">
                        Su reclamo está siendo atendido. La resolución formal será comunicada 
                        a su correo electrónico una vez que sea aprobada.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Indicador de respuesta enviada al reclamante */}
            {resultado.reclamo.respuesta_enviada_reclamante && (
              <Card className="border-green-200 bg-green-50/80">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-800">Respuesta enviada por correo al reclamante</p>
                    {resultado.reclamo.fecha_envio_respuesta_reclamante && (
                      <p className="text-sm text-green-700">
                        Fecha de envío: {new Date(resultado.reclamo.fecha_envio_respuesta_reclamante).toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Footer informativo */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Si tiene dudas sobre su reclamo, puede comunicarse con nosotros a través de
            los canales oficiales de {empresa.nombre}.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4 mt-auto">
        <div className="max-w-3xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>Libro de Reclamaciones Virtual</p>
        </div>
      </footer>
    </div>
  );
};

export default ConsultaReclamoPage;
